import { h } from 'preact';
import { ChangeEvent } from 'preact/compat';
import { useContext, useEffect, useState } from 'preact/hooks';
import { ToastServiceContext, ToastType } from '@uw/common-components';
import { headerStore } from 'global/header.store';
import { stateStore } from 'global/state.store';
import { useTranslation } from 'hooks';
import { usePasswordReset } from 'hooks/use-password-reset';
import { Button } from 'components/button';
import { NEW_LOGIN_STATE, RECOVERY_STATE } from 'constants/constants';
import './forgot-password.scss';

const TOAST_SHOW_TIMEOUT = 6000;
const FORGOT_EMAIL_PASSWORD_FIELD = 'email';

export const ForgotPassword = () => {
  const { translate } = useTranslation();
  const { showToast } = useContext(ToastServiceContext);
  const { sendResetEmail, forgotErrors, clearForgotError, isLoadingForgot } = usePasswordReset();
  const [email, setEmail] = useState<string>('');

  const submitBtnTextPath = isLoadingForgot
    ? 'widget.forgot.buttons.processing'
    : 'widget.forgot.buttons.reset';

  const handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.currentTarget.value);
  };

  const resetFieldError = () => {
    clearForgotError(FORGOT_EMAIL_PASSWORD_FIELD);
  };

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();

    const result = await sendResetEmail(email);

    if (result.success) {
      stateStore.state.next(RECOVERY_STATE);
      showToast(ToastType.Success, translate('widget.forgot.emailSent', { email }), {
        duration: TOAST_SHOW_TIMEOUT,
      });
    } else if (result.accountError) {
      const translatedMessage = translate(result.accountError);
      // Parse the HTML string and convert the link to JSX
      const emailMatch = translatedMessage.match(/<a[^>]*href="([^"]*)"[^>]*>([^<]*)<\/a>/);

      if (emailMatch) {
        const [fullMatch, href, linkText] = emailMatch;
        const [textBeforeLink, textAfterLink] = translatedMessage.split(fullMatch);

        showToast(
          ToastType.Error,
          <span>
            {textBeforeLink}
            <a href={href}>{linkText}</a>
            {textAfterLink}
          </span>,
          { duration: TOAST_SHOW_TIMEOUT },
        );
      } else {
        showToast(ToastType.Error, translatedMessage, { duration: TOAST_SHOW_TIMEOUT });
      }
    }
  };

  useEffect(() => {
    headerStore.backState.next(NEW_LOGIN_STATE);
    headerStore.title.next('widget.forgot.title');
  }, []);

  return (
    <div className="uw-forgot">
      <form
        className="uw-forgot__form"
        name="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit}
      >
        <fieldset>
          <div
            className={`uw-forgot__form-group ${forgotErrors.email ? 'uw-forgot__form-group_error' : ''} ${email ? 'uw-forgot__form-group_filled' : ''}`}
          >
            <label htmlFor="email-field" id="forgot-email-label" className="uw-forgot__label">
              {translate('widget.forgot.labels.email')}
            </label>
            <input
              id="email-field"
              name="email"
              className="uw-forgot__input-field"
              type="email"
              title={translate('widget.login.titles.enter_email')}
              value={email}
              onChange={handleFieldChange}
              onFocus={resetFieldError}
              aria-required="true"
              aria-describedby={forgotErrors.email ? 'forgot-email-err' : undefined}
              aria-invalid={!!forgotErrors.email}
            />
            {forgotErrors.email && (
              <p id="forgot-email-err" role="alert" className="uw-forgot__input-field-error">
                {forgotErrors.email}
              </p>
            )}
          </div>
        </fieldset>

        <div className="uw-forgot__btn-row">
          <Button
            className="uw-forgot__button"
            type="submit"
            aria-label={translate('widget.forgot.arias.reset')}
            aria-busy={isLoadingForgot}
            disabled={isLoadingForgot}
          >
            {translate(submitBtnTextPath)}
          </Button>
        </div>
      </form>
    </div>
  );
};
